import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { useContext, useEffect, useState } from 'react'

import {
  SubstanceCounterContext,
  SubstanceCounterContextInterface,
  SubstanceWithUnitsDrugForms,
} from '../../../providers/SubstanceCounter/SubstanceCounter.Provider'
import { themeColors } from '../../../theme'
import { Modal } from '../../Modal/Modal'
import { useStyles } from '../PatientChartPage.styles'

interface TrackedSubstanceForm {
  substanceId: number
  dosageUnitId: number
  drugFormId?: number
  availableUnits?: string[]
  availableDrugForms?: string[]
}

interface CreateTrackedSubstanceModalInterface {
  routedPatientId: string | undefined
  showCreateTrackedSubstanceModal: boolean
  setShowCreateTrackedSubstanceModal: React.Dispatch<
    React.SetStateAction<boolean>
  >
}

export const CreateTrackedSubstanceModal = ({
  routedPatientId,
  showCreateTrackedSubstanceModal,
  setShowCreateTrackedSubstanceModal,
}: CreateTrackedSubstanceModalInterface) => {
  const { classes } = useStyles()

  const {
    substancesWithUnitsDrugForms,
    getSubstancesWithUnitsDrugForms,
    createTrackedSubstance,
    getPatientTrackedSubstancesForProvider,
  } = useContext(SubstanceCounterContext) as SubstanceCounterContextInterface

  const [formData, setFormData] = useState<TrackedSubstanceForm>({
    substanceId: 0,
    dosageUnitId: 0,
    drugFormId: 0,
  })

  const [selectedSubstance, setSelectedSubstance] =
    useState<SubstanceWithUnitsDrugForms | null>(null)

  const [loading, setLoading] = useState(false)

  useEffect(() => {
    getSubstancesWithUnitsDrugForms()
  }, [])

  const handleSubstanceChange = (
    substance: SubstanceWithUnitsDrugForms | null
  ) => {
    setSelectedSubstance(substance)

    setFormData((prev) => ({
      ...prev,
      substanceId: substance?.substanceId || 0,
      dosageUnitId: 0, // Reset unit selection when substance changes
      drugFormId: 0, // Reset drug form selection when substance changes
    }))
  }

  const handleDrugFormChange = (drugFormId: number) => {
    setFormData((prev) => ({
      ...prev,
      drugFormId: drugFormId,
    }))
  }

  const handleUnitChange = (unitId: number) => {
    setFormData((prev) => ({
      ...prev,
      dosageUnitId: unitId,
    }))
  }

  const handleCreate = async () => {
    setLoading(true)

    // Form Validation
    if (
      !formData.substanceId ||
      !formData.dosageUnitId ||
      !formData.drugFormId
    ) {
      alert('Please fill in all required fields')
      setLoading(false)
      return
    }

    if (routedPatientId) {
      const request = {
        ...formData,
        patientId: parseInt(routedPatientId),
        isActive: true,
      }
      await createTrackedSubstance(request)
      await getPatientTrackedSubstancesForProvider(parseInt(routedPatientId))
    }

    setLoading(false)
    setShowCreateTrackedSubstanceModal(false)
    setFormData({
      substanceId: 0,
      dosageUnitId: 0,
      drugFormId: 0,
    })
    setSelectedSubstance(null)
  }

  const handleCancel = () => {
    // Reset form and close modal
    setFormData({
      substanceId: 0,
      dosageUnitId: 0,
      drugFormId: 0,
    })
    setSelectedSubstance(null)
    setShowCreateTrackedSubstanceModal(false)
  }

  return (
    <Modal
      open={showCreateTrackedSubstanceModal}
      setOpen={setShowCreateTrackedSubstanceModal}
      title={'Create New Tracked Substance'}
      width={'40%'}
      ModalContent={
        <Box
          sx={{
            width: '92%',
            margin: 'auto',
            padding: '10px 0',
          }}
        >
          <Typography
            sx={{
              fontSize: '20px',
              lineHeight: '25px',
              color: themeColors.grayblack,
              padding: '10px 0 20px 0',
            }}
          >
            Please fill up the form to create new tracked substances.
          </Typography>

          {/* Substance Autocomplete Selection */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <Autocomplete
              options={substancesWithUnitsDrugForms || []}
              getOptionLabel={(option) => option.substanceName}
              value={selectedSubstance}
              onChange={(_, newValue) => handleSubstanceChange(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Select Substance *'
                  variant='outlined'
                  placeholder='Start typing to search substances...'
                />
              )}
              filterOptions={(options, params) => {
                const { inputValue } = params
                return options.filter((option) =>
                  option.substanceName
                    .toLowerCase()
                    .includes(inputValue.toLowerCase())
                )
              }}
              isOptionEqualToValue={(option, value) =>
                option.substanceId === value.substanceId
              }
              noOptionsText='No substances found.'
            />
          </FormControl>

          {/* Drug form Selection - Only show when substance is selected */}
          {selectedSubstance &&
            selectedSubstance.availableDrugForms.length > 0 && (
              <Box
                sx={{
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  p: 2,
                  mb: 3,
                }}
              >
                <Typography
                  variant='h6'
                  sx={{ mb: 2, color: themeColors.grayblack, p: 0 }}
                >
                  Drug Form Information
                </Typography>

                <Grid item xs={12}>
                  <Typography
                    variant='subtitle2'
                    sx={{ fontWeight: 'bold', marginBottom: '8px' }}
                  >
                    Drug Form *
                  </Typography>

                  <FormControl fullWidth>
                    <Select
                      value={formData.drugFormId || ''}
                      onChange={(e) =>
                        handleDrugFormChange(Number(e.target.value))
                      }
                      displayEmpty
                      style={{ height: '52px' }}
                    >
                      <MenuItem value='' disabled>
                        Select a drug form
                      </MenuItem>

                      {selectedSubstance.availableDrugForms.map(
                        (drugForm, i) => (
                          <MenuItem
                            key={`drugForm-${i}`}
                            value={drugForm.drugFormId}
                          >
                            {drugForm.drugFormName}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              </Box>
            )}

          {/* Dosage Unit Selection - Only show when substance is selected */}
          {selectedSubstance && selectedSubstance.availableUnits.length > 0 && (
            <Box
              sx={{
                border: '1px solid #ccc',
                borderRadius: '4px',
                p: 2,
                mb: 3,
              }}
            >
              <Typography
                variant='h6'
                sx={{ mb: 2, color: themeColors.grayblack, p: 0 }}
              >
                Dosage Information
              </Typography>

              <Grid item xs={12}>
                <Typography
                  variant='subtitle2'
                  sx={{ fontWeight: 'bold', marginBottom: '8px' }}
                >
                  Dosage Unit *
                </Typography>

                <FormControl fullWidth>
                  <Select
                    value={formData.dosageUnitId || ''}
                    onChange={(e) => handleUnitChange(Number(e.target.value))}
                    displayEmpty
                    style={{ height: '52px' }}
                  >
                    <MenuItem value='' disabled>
                      Select a unit
                    </MenuItem>

                    {selectedSubstance.availableUnits.map((unitObj, i) => (
                      <MenuItem key={`unit-${i}`} value={unitObj.dosageUnitId}>
                        {unitObj.unitName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Box>
          )}

          <div className={classes.modalBtnWrapper}>
            <Button
              onClick={handleCreate}
              variant='contained'
              disabled={
                !formData.substanceId || !formData.dosageUnitId || loading
              }
            >
              {loading ? (
                <CircularProgress size={20} color='primary' />
              ) : (
                'Create'
              )}
            </Button>

            <Button
              onClick={handleCancel}
              variant='outlined'
              style={{
                fontSize: '16px',
                marginTop: '10px',
                width: '80px',
              }}
            >
              Cancel
            </Button>
          </div>
        </Box>
      }
    />
  )
}
